import React, { useState } from 'react';
import { DollarSign, TrendingUp, Calculator } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { trackLead } from '../utils/fbPixel';

const ROICalculator = () => {
  const [investment, setInvestment] = useState(15000000); // Default Rp 15 juta
  const [strategy, setStrategy] = useState('moderate');

  const handleInvestmentChange = (value) => {
    setInvestment(value);
  };
  
  const strategies = {
    conservative: { monthly: 5, name: 'Konservatif (5%/bulan)' },
    moderate: { monthly: 12, name: 'Moderat (12%/bulan)' },
    aggressive: { monthly: 20, name: 'Agresif (20%/bulan)' }
  };

  const calculateReturns = (months) => {
    const monthlyRate = strategies[strategy].monthly / 100;
    const total = investment * Math.pow(1 + monthlyRate, months);
    const profit = total - investment;
    return {
      total: total.toFixed(0),
      profit: profit.toFixed(0)
    };
  };

  const formatRupiah = (number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(number);
  };

  const returns3M = calculateReturns(3);
  const returns6M = calculateReturns(6);
  const returns12M = calculateReturns(12);

  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  return (
    <Card className="roi-calculator-card" style={{
      maxWidth: isMobile ? '100%' : '800px',
      margin: '0 auto',
      padding: isMobile ? '16px' : '24px'
    }}>
      <CardHeader style={{ padding: isMobile ? '16px 0' : '24px 0' }}>
        <CardTitle className="calculator-title" style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          fontSize: isMobile ? '20px' : '28px',
          justifyContent: 'center',
          textAlign: 'center'
        }}>
          <Calculator className="title-icon" style={{ 
            width: isMobile ? '24px' : '32px',
            height: isMobile ? '24px' : '32px'
          }} />
          Hitung Potensi Cuan Kamu
        </CardTitle>
      </CardHeader>
      <CardContent style={{ padding: 0 }}>
        <div className="calculator-inputs" style={{
          display: 'flex',
          flexDirection: 'column',
          gap: isMobile ? '16px' : '24px',
          marginBottom: isMobile ? '24px' : '32px'
        }}>
          <div className="input-group">
            <label className="input-label" style={{
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              marginBottom: '8px',
              display: 'block'
            }}>Modal Awal (Rupiah)</label>
            <div className="input-wrapper" style={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center'
            }}>
              <DollarSign className="input-icon" style={{
                position: 'absolute',
                left: '12px',
                width: '18px',
                height: '18px',
                color: 'var(--medium-gray)'
              }} />
              <input
                type="number"
                className="calculator-input"
                value={investment}
                onChange={(e) => handleInvestmentChange(Number(e.target.value))}
                min="1000000"
                step="1000000"
                style={{
                  width: '100%',
                  padding: isMobile ? '12px 12px 12px 40px' : '14px 16px 14px 44px',
                  fontSize: isMobile ? '14px' : '15px',
                  borderRadius: '8px',
                  border: '1px solid rgba(227, 24, 55, 0.2)',
                  background: 'var(--light-gray)'
                }}
              />
            </div>
          </div>

          <div className="input-group">
            <label className="input-label" style={{
              fontSize: isMobile ? '13px' : '14px',
              fontWeight: 600,
              marginBottom: '8px',
              display: 'block'
            }}>Tipe Strategi</label>
            <select
              className="calculator-select"
              value={strategy}
              onChange={(e) => setStrategy(e.target.value)}
              style={{
                width: '100%',
                padding: isMobile ? '12px' : '14px 16px',
                fontSize: isMobile ? '14px' : '15px',
                borderRadius: '8px',
                border: '1px solid rgba(227, 24, 55, 0.2)',
                background: 'var(--light-gray)',
                cursor: 'pointer'
              }}
            >
              <option value="conservative">{strategies.conservative.name}</option>
              <option value="moderate">{strategies.moderate.name}</option>
              <option value="aggressive">{strategies.aggressive.name}</option>
            </select>
          </div>
        </div>

        <div className="calculator-results" style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: isMobile ? '12px' : '20px',
          marginBottom: isMobile ? '20px' : '24px'
        }}>
          <div className="result-item" style={{
            background: 'var(--light-gray)',
            border: '1px solid rgba(227, 24, 55, 0.1)',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}>
            <div className="result-period" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--medium-gray)',
              marginBottom: '8px',
              fontWeight: 500,
              textTransform: 'uppercase'
            }}>3 Bulan</div>
            <div className="result-profit" style={{
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: 700,
              color: 'var(--success-green)',
              marginBottom: '6px'
            }}>+{formatRupiah(returns3M.profit)}</div>
            <div className="result-total" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--dark-gray)',
              fontWeight: 500
            }}>Total: {formatRupiah(returns3M.total)}</div>
          </div>

          <div className="result-item" style={{
            background: 'var(--light-gray)',
            border: '1px solid rgba(227, 24, 55, 0.1)',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            textAlign: 'center',
            transition: 'all 0.3s ease'
          }}>
            <div className="result-period" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--medium-gray)',
              marginBottom: '8px',
              fontWeight: 500,
              textTransform: 'uppercase'
            }}>6 Bulan</div>
            <div className="result-profit" style={{
              fontSize: isMobile ? '18px' : '24px',
              fontWeight: 700,
              color: 'var(--success-green)',
              marginBottom: '6px'
            }}>+{formatRupiah(returns6M.profit)}</div>
            <div className="result-total" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--dark-gray)',
              fontWeight: 500
            }}>Total: {formatRupiah(returns6M.total)}</div>
          </div>

          <div className="result-item highlighted" style={{
            background: 'linear-gradient(135deg, rgba(227, 24, 55, 0.05) 0%, rgba(227, 24, 55, 0.02) 100%)',
            border: '2px solid var(--primary-red)',
            borderRadius: '12px',
            padding: isMobile ? '16px' : '24px',
            textAlign: 'center',
            position: 'relative',
            transition: 'all 0.3s ease'
          }}>
            <div className="result-period" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--primary-red)',
              marginBottom: '8px',
              fontWeight: 600,
              textTransform: 'uppercase'
            }}>12 Bulan ⭐</div>
            <div className="result-profit" style={{
              fontSize: isMobile ? '20px' : '28px',
              fontWeight: 700,
              color: 'var(--primary-red)',
              marginBottom: '6px'
            }}>+{formatRupiah(returns12M.profit)}</div>
            <div className="result-total" style={{
              fontSize: isMobile ? '11px' : '13px',
              color: 'var(--dark-gray)',
              fontWeight: 500
            }}>Total: {formatRupiah(returns12M.total)}</div>
            <TrendingUp className="result-icon" style={{
              position: 'absolute',
              top: isMobile ? '12px' : '16px',
              right: isMobile ? '12px' : '16px',
              width: isMobile ? '16px' : '20px',
              height: isMobile ? '16px' : '20px',
              color: 'var(--primary-red)'
            }} />
          </div>
        </div>

        <div className="calculator-disclaimer" style={{
          padding: isMobile ? '12px' : '16px',
          background: 'var(--light-gray)',
          borderLeft: '4px solid var(--primary-red)',
          borderRadius: '4px',
          marginBottom: isMobile ? '20px' : '24px'
        }}>
          <p style={{
            fontSize: isMobile ? '11px' : '12px',
            color: 'var(--medium-gray)',
            margin: 0,
            lineHeight: 1.5
          }}>* Performa masa lalu tidak menjamin hasil di masa depan. Kalkulasi berdasarkan rata-rata return.</p>
        </div>

        <Button className="btn-primary-sahara btn-large calculator-cta" onClick={() => {
          trackLead('ROI Calculator CTA');
          window.open('https://t.me/saharacopytrade', '_blank');
        }} style={{
          width: '100%',
          padding: isMobile ? '14px 24px' : '16px 36px',
          fontSize: isMobile ? '15px' : '17px'
        }}>
          Mulai Copy Trading
        </Button>
      </CardContent>
    </Card>
  );
};

export default ROICalculator;
